// src/components/Multiplayer/CashRummy/CashRummy.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../../../contexts/WebSocketContext';
import { useWallet } from '../../../contexts/WalletContext';
import { useAuth } from '../../../contexts/AuthContext';
import CurrencyService from '../../../services/CurrencyService';
import ActiveRoomsDropdown from '../ActiveRoomsDropdown';
import './CashRummy.css';

// Every game room (free play, cash, custom) is played by 2, 4 or 6 players.
const PLAYER_COUNTS = [2, 4, 6];

const GAME_MODES = [
  {
    id: 'deal',
    title: 'Deal Rummy',
    desc: 'Fixed deals — lowest total score wins',
    icon: '🃏',
    defaults: { totalDeals: 2, maxPlayers: 2 },
    settings: [
      { key: 'totalDeals', label: 'Number of Deals', options: [2, 4] },
      { key: 'maxPlayers', label: 'Players', options: PLAYER_COUNTS },
    ]
  },
  {
    id: 'points',
    title: 'Pool Rummy',
    desc: 'Reach target points to eliminate opponents',
    icon: '🏆',
    defaults: { maxPoints: 201, maxPlayers: 6 },
    settings: [
      { key: 'maxPlayers', label: 'Players', options: PLAYER_COUNTS },
      { key: 'maxPoints', label: 'Target Points', options: [101, 201, 301] },
    ]
  },
  {
    id: 'rate',
    title: 'Points Rummy',
    desc: 'One hand · pay per point · max 80 points',
    icon: '🎯',
    defaults: { maxPlayers: 2 },
    settings: [
      { key: 'maxPlayers', label: 'Players', options: PLAYER_COUNTS },
    ]
  }
];

const BUY_IN_AMOUNTS = [1, 2, 5, 10, 50];
// Points Rummy: USD per point. The player only needs 80 x this in the wallet; it is charged per point at the end.
const RATE_AMOUNTS = [0.05, 0.10, 0.20, 0.25, 0.50, 1.00];
const RATE_MAX_POINTS = 80;

// Map a currency code to the wallet balance key. WalletContext stores non-USD/INR/LRD
// local currencies (GMD, GHS, NGN, SLE, XOF, CFA, ...) under the `LOCAL` key.
const getBalanceKey = (currency) => {
  const c = (currency || 'USD').toUpperCase();
  if (c === 'USD') return 'USD';
  if (c === 'INR') return 'INR';
  if (c === 'LRD') return 'LRD';
  return 'LOCAL';
};

const CashRummy = () => {
  const navigate = useNavigate();
  const { isConnected, joinCashGame, socket } = useWebSocket();
  const { walletBalances, walletData } = useWallet();
  const { currentUser } = useAuth();

  const [selectedMode, setSelectedMode] = useState(null);
  const [config, setConfig] = useState({});
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [buyInAmount, setBuyInAmount] = useState(BUY_IN_AMOUNTS[0]);
  const [convertedAmount, setConvertedAmount] = useState(0);
  const [currencyService, setCurrencyService] = useState(null);
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState('');
  
  // Get joinCashRummy from context
  const { joinCashRummy } = useWebSocket();

  // Initialize currency service
  useEffect(() => {
    const initService = async () => {
      const service = CurrencyService;
      await service.getRates();
      setCurrencyService(service);
    };
    initService();
  }, []);

  // Calculate converted amount
  useEffect(() => {
    if (currencyService && selectedCurrency) {
      const converted = currencyService.convertFromUSD(buyInAmount, selectedCurrency);
      setConvertedAmount(converted);
    }
  }, [buyInAmount, selectedCurrency, currencyService]);

  // Determine the user's local currency from their country (same logic as the Header,
  // so the cash-game currency options match the wallet balance display).
  const getLocalCurrency = () => {
    const userCurrency = String(currentUser?.currencyCode || currentUser?.currency || '').toUpperCase();
    if (userCurrency && userCurrency !== 'USD' && userCurrency !== 'LOCAL') {
      return userCurrency;
    }
    const countryCode = String(walletData?.countryCode || currentUser?.country || '').toUpperCase();
    if (countryCode === 'BJ') return 'CFA';
    if (countryCode === 'LR' || countryCode === 'LBR') return 'LRD';
    if (countryCode === 'GM') return 'GMD';
    if (countryCode === 'IN') return 'INR';
    return 'USD';
  };

  const localCurrency = getLocalCurrency();

  // Currency options: USD, INR, and the user's local currency (LRD / GMD / CFA ...).
  const availableCurrencies = [...new Set(['USD', 'INR', localCurrency])].filter(Boolean);

  // Default the selection to the user's own currency.
  useEffect(() => {
    setSelectedCurrency(localCurrency);
  }, [localCurrency]);

  const isRate = selectedMode === 'rate';
  // Points Rummy needs 80 x the amount per point in the wallet; every other game needs its buy-in.
  const requiredUSD = isRate ? Math.round(RATE_MAX_POINTS * buyInAmount * 100) / 100 : buyInAmount;
  const requiredLocal = currencyService && selectedCurrency
    ? currencyService.convertFromUSD(requiredUSD, selectedCurrency)
    : requiredUSD;

  const handleModeSelect = (modeId) => {
    const mode = GAME_MODES.find(m => m.id === modeId);
    setSelectedMode(modeId);
    setConfig({ ...mode.defaults });
    setError('');
    // switching between Points Rummy (cents per point) and the buy-in games keeps a valid amount
    if (modeId === 'rate' && !RATE_AMOUNTS.includes(buyInAmount)) setBuyInAmount(RATE_AMOUNTS[1]);
    if (modeId !== 'rate' && !BUY_IN_AMOUNTS.includes(buyInAmount)) setBuyInAmount(BUY_IN_AMOUNTS[0]);
  };

  const updateSetting = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const checkBalance = () => {
    const balance = walletBalances[getBalanceKey(selectedCurrency)] || 0;
    // Points Rummy: at least 80 x the amount per point (nothing is taken when joining)
    return balance >= (selectedMode === 'rate' ? requiredLocal : convertedAmount);
  };

  const joinCashGameAction = useCallback(async () => {
    if (!currentUser) {
      setError('Please log in to play cash games.');
      return;
    }
    if (!isConnected || !socket) {
      setError('Not connected to server.');
      return;
    }
    if (!selectedMode) {
      setError('Please select a game mode.');
      return;
    }
    if (!checkBalance()) {
      setError(selectedMode === 'rate'
        ? `Insufficient funds. Points Rummy needs at least ${currencyService?.format(requiredLocal, selectedCurrency) || requiredUSD} in your wallet (${RATE_MAX_POINTS} x the amount per point).`
        : `Insufficient balance. You need ${currencyService?.format(convertedAmount, selectedCurrency) || buyInAmount} ${selectedCurrency}`);
      return;
    }

    setIsJoining(true);
    setError('');

    try {
      const options = {
        gameMode: selectedMode,
        entryFee: convertedAmount,
        currency: selectedCurrency,
        buyInUSD: buyInAmount,
        ...config
      };

      // Use the joinCashRummy method from WebSocket context
      const result = await joinCashRummy(options);

      if (result && result.success && result.roomCode) {
        // Navigate directly io persistent cash game table
        const gameId = result.gameId || result.roomCode;
        const maxP = result.maxPlayers || config.maxPlayers || 2;
        const totalPot = convertedAmount * maxP;
        // Points Rummy has no fixed prize (it depends on the points), so no "win amount" is shown for it.
        const winAmt = selectedMode === 'rate' ? null : totalPot * 0.90;
        navigate(`/game/${gameId}`, {
          state: {
            roomCode: result.roomCode,
            gameId: result.gameId,
            entryFee: convertedAmount,
            currency: selectedCurrency,
            buyInUSD: buyInAmount,
            gameMode: selectedMode,
            seatPosition: result.seatPosition,
            playerCount: result.playerCount,
            maxPlayers: maxP,
            isCashGame: true,
            winningAmount: winAmt,
            ...config
          }
        });
      } else {
        setError(result?.message || 'Failed to join cash game.');
      }
    } catch (err) {
      setError(err.message || 'Failed to join cash game.');
    } finally {
      setIsJoining(false);
    }
  }, [currentUser, isConnected, socket, selectedMode, config, convertedAmount, selectedCurrency, buyInAmount, currencyService, navigate, joinCashRummy, checkBalance]);

  // Join a specific room picked from the active-rooms dropdown.
  const joinSpecificRoom = useCallback(async (room) => {
    if (!currentUser) { setError('Please log in to play cash games.'); return; }
    if (!isConnected || !socket) { setError('Not connected to server.'); return; }
    setIsJoining(true);
    setError('');
    try {
      // Every player pays in THEIR OWN wallet currency (selectedCurrency), not the room creator's.
      // The server takes the canonical USD buy-in from the room itself, so don't send the creator's
      // local amount as USD.
      const options = {
        roomCode: room.roomCode,
        gameMode: room.gameMode,
        entryFee: room.betAmount,
        currency: selectedCurrency || room.currency,
        buyInUSD: 0,
        totalDeals: room.deals || 2,
        maxPoints: room.points || 201,
        maxPlayers: room.maxPlayers || 2
      };
      const result = await joinCashRummy(options);
      if (result && result.success && result.roomCode) {
        const gameId = result.gameId || result.roomCode;
        const maxP = room.maxPlayers || 2;
        const totalPot = room.betAmount * maxP;
        const winAmt = room.gameMode === 'rate' ? null : totalPot * 0.90;
        navigate(`/game/${gameId}`, {
          state: {
            roomCode: result.roomCode,
            gameId: result.gameId,
            entryFee: room.betAmount,
            currency: selectedCurrency || room.currency,
            buyInUSD: result.buyInUSD || 0,
            gameMode: room.gameMode,
            seatPosition: result.seatPosition,
            playerCount: result.playerCount,
            maxPlayers: maxP,
            isCashGame: true,
            winningAmount: winAmt
          }
        });
      } else {
        setError(result?.message || 'Failed to join cash game.');
      }
    } catch (err) {
      setError(err.message || 'Failed to join cash game.');
    } finally {
      setIsJoining(false);
    }
  }, [currentUser, isConnected, socket, joinCashRummy, navigate, selectedCurrency]);

  const selectedModeData = GAME_MODES.find(m => m.id === selectedMode);

  if (!currentUser) {
    return (
      <div className="cr-container">
        <div className="cr-auth-required">
          <h2>🔒 Please login to play Cash Rummy</h2>
          <button onClick={() => navigate('/login')}>Login / Sign Up</button>
        </div>
      </div>
    );
  }

  return (
    <div className="cr-container">
      {/* Header */}
      <div className="cr-header">
        {/* Step 2 -> back to the game list; step 1 -> back to the lobby */}
        <button className="cr-back-btn" onClick={() => (selectedMode ? setSelectedMode(null) : navigate('/multiplayer'))}>
          ← Back
        </button>
        <div className="cr-header-center">
          <h1>{selectedModeData ? `${selectedModeData.icon} ${selectedModeData.title}` : '💰 Cash Rummy'}</h1>
          <p className="cr-subtitle">{selectedModeData ? selectedModeData.desc : 'Choose a game'}</p>
        </div>
        <div className="cr-wallet">
          <span className="cr-currency-symbol">{currencyService?.getSymbol(selectedCurrency) || '$'}</span>
          <span className="cr-balance">{Number(walletBalances[getBalanceKey(selectedCurrency)] || 0).toFixed(2)}</span>
        </div>
      </div>

      {/* Connection Status */}
      <div className={`cr-connection ${isConnected ? 'connected' : ''}`}>
        {isConnected ? '🟢 Connected' : '🔴 Disconnected'}
      </div>

      {/* Error Display */}
      {error && (
        <div className="cr-error">
          <span>{error}</span>
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* STEP 1: choose the game (Deal / Pool / Points Rummy) - nothing else is shown yet */}
      {!selectedMode && (
        <div className="cr-modes">
          <h3>Select Game</h3>
          <div className="cr-mode-cards">
            {GAME_MODES.map(mode => (
              <div
                key={mode.id}
                className="cr-mode-card"
                onClick={() => handleModeSelect(mode.id)}
              >
                <span className="cr-mode-icon">{mode.icon}</span>
                <div className="cr-mode-info">
                  <h4>{mode.title}</h4>
                  <p>{mode.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* STEP 2: the chosen game's own details - rooms, currency, amount, deals / points, players */}
      {selectedMode && (<>

      {/* Active Rooms Dropdown (only this game's rooms) */}
      <ActiveRoomsDropdown kind="cash" onJoin={joinSpecificRoom} gameMode={selectedMode} />

      {/* Currency Selection */}
      <div className="cr-currency-bar">
        <span className="cr-currency-label">Currency:</span>
        <div className="cr-currency-options">
          {availableCurrencies.map(currency => (
            <button
              key={currency}
              className={`cr-currency-btn ${selectedCurrency === currency ? 'active' : ''}`}
              onClick={() => setSelectedCurrency(currency)}
            >
              {currency}
            </button>
          ))}
        </div>
      </div>

      {/* Buy-in Selection */}
      <div className="cr-buyin-section">
        <h3>{isRate ? 'Select Amount per Point' : 'Select Buy-in Amount'}</h3>
        <div className="cr-buyin-grid">
          {(isRate ? RATE_AMOUNTS : BUY_IN_AMOUNTS).map(amount => (
            <button
              key={amount}
              className={`cr-buyin-btn ${buyInAmount === amount ? 'active' : ''}`}
              onClick={() => setBuyInAmount(amount)}
            >
              <span className="cr-buyin-usd">${isRate ? amount.toFixed(2) : amount} USD{isRate ? ' / pt' : ''}</span>
              {currencyService && selectedCurrency !== 'USD' && (
                <span className="cr-buyin-converted">
                  ≈ {currencyService.format(currencyService.convertFromUSD(amount, selectedCurrency), selectedCurrency)}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Balance Check */}
        {currencyService && (
          <div className={`cr-balance-check ${checkBalance() ? 'sufficient' : 'insufficient'}`}>
            {isRate ? (
              <span>
                Nothing is charged now. You pay per point at the end (max {RATE_MAX_POINTS} points).
                Minimum balance: {currencyService.format(requiredLocal, selectedCurrency)}
              </span>
            ) : (
              <span>You will pay: {currencyService.format(convertedAmount, selectedCurrency)}</span>
            )}
            <span className="cr-balance-status">
              {checkBalance() ? '✅ Sufficient Balance' : '❌ Insufficient Balance'}
            </span>
          </div>
        )}
      </div>

      {/* Settings (deals / target points / players of the chosen game) */}
      {selectedModeData && (
        <div className="cr-settings">
          {selectedModeData.settings.map(setting => (
            <div key={setting.key} className="cr-setting-row">
              <label>{setting.label}</label>
              <div className="cr-option-btns">
                {setting.options.map(opt => (
                  <button
                    key={opt}
                    className={`cr-option-btn ${config[setting.key] === opt ? 'active' : ''}`}
                    onClick={() => updateSetting(setting.key, opt)}
                  >
                    {setting.key === 'maxPoints' ? `${opt} Pts` : setting.key === 'totalDeals' ? `${opt} Deals` : `${opt}`}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Join Button */}
      <button
        className={`cr-play-btn ${!isConnected || isJoining || !selectedMode ? 'disabled' : ''}`}
        onClick={joinCashGameAction}
        disabled={!isConnected || isJoining || !selectedMode}
      >
        {isJoining ? (
          <><span className="cr-spinner" /> Joining Cash Game...</>
        ) : (
          `Play ${selectedModeData ? selectedModeData.title : 'Cash Rummy'}`
        )}
      </button>

      </>)}

    </div>
  );
};

export default CashRummy;
